/**
 * Missions Domain
 * Types, store and handlers for sequential team missions.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { handleTeamUpdate } from './teams';
import type { Team } from './teams';
import { getCurrentPlayer, getViewerUserId } from './current-team';
import { formatCash, formatNumber } from '../utils/format';

// ==================== TYPES ====================

/** Mission type categories */
export const MissionType = {
  OperatingMarginRank: 1,
  AnnualProfit: 2,
  PassengerVolume: 3,
  PassengerMiles: 4,
  CountryReach: 5,
  Headquarters: 6,
  RivalShares: 7,
  LongHaulRoute: 8,
  FleetDiversity: 9,
  PremiumPassengers: 10,
  LoadFactor: 11,
  FleetSize: 12,
  HubDevelopment: 13,
  RevenueTarget: 14,
  ComfortScore: 15,
  RouteCount: 16,
} as const;

export type MissionType = (typeof MissionType)[keyof typeof MissionType];

/** Mission instance */
export interface Mission {
  mission_id: number;
  definition_id: string;
  type: MissionType;
  title_key: string;
  description_key: string;
  target_value: number;
  current_progress: number;
}

/** Track metadata entry for roadmap UI */
export interface MissionTrackStep {
  step: number;
  definition_id: string;
  type: MissionType;
  title_key: string;
  description_key: string;
  target_value: number;
}

/** Stretch goal definition from the session-script */
export interface SessionScriptStretchGoal {
  metric: string;
  descriptionKey: string;
  valueKey: string;
  explanationKey: string;
}

/** Team mission store from server */
export interface TeamMissionStore {
  team_id: number;
  active_missions: Mission[];
  completed_count: number;
  next_mission_id: number;
  completed_definitions: Record<string, boolean>;
  version: number;
  current_step: number;
  total_steps: number;
  track: MissionTrackStep[];
  /** Ordered list of mission definition IDs from the session-script. */
  session_script_mission_ids?: string[];
  /** Open-ended goal shown after all session-script missions are complete. */
  stretch_goal?: SessionScriptStretchGoal;
}

/** A step of the track as a mission's celebration shows it. */
export interface CelebratedStep {
  id: string;
  descriptionKey: string;
  done: boolean;
  /** The mission the celebration is for. */
  justDone: boolean;
}

/** A mission done, as its celebration shows it: the track as it stood the moment it was done. */
export interface MissionCelebration {
  id: string;
  mission: Mission;
  /** How many of the track's missions are done, the one celebrated included. */
  done: number;
  total: number;
  track: CelebratedStep[];
}

// Message type interfaces
interface MissionsStateMessage {
  type: string;
  cohort_id: string;
  mission_store: TeamMissionStore;
}

interface MissionProgressMessage {
  type: string;
  cohort_id: string;
  missions: Mission[];
}

interface MissionCompletedMessage {
  type: string;
  cohort_id: string;
  mission: Mission;
  team: Team;
  mission_store: TeamMissionStore;
}

// ==================== STORE ====================

interface MissionsState {
  missionsByTeam: Record<string, TeamMissionStore>;
  celebrationQueue: MissionCelebration[];
  /** Stretch goal celebration shown after all episode missions are complete. */
  stretchGoalCelebration: SessionScriptStretchGoal | null;

  setMissionStore: (cohortId: string, store: TeamMissionStore) => void;
  updateMission: (cohortId: string, mission: Mission) => void;
  enqueueCelebration: (celebration: MissionCelebration) => void;
  dismissCelebration: () => void;
  setStretchGoalCelebration: (goal: SessionScriptStretchGoal) => void;
  dismissStretchGoalCelebration: () => void;
  reset: () => void;
}

const initialState = {
  missionsByTeam: {} as Record<string, TeamMissionStore>,
  celebrationQueue: [] as MissionCelebration[],
  stretchGoalCelebration: null as SessionScriptStretchGoal | null,
};

/** A step of the team's track as the missions card shows it. */
export interface TrackStep {
  id: string;
  descriptionKey: string;
  state: 'done' | 'active' | 'next';
  /** The active step's progress: how far, of how much, and the fraction of the way (0 to 1). */
  progress?: { current: number; target: number; fraction: number };
}

/**
 * The team's mission track as the missions card shows it: every step done,
 * in progress or still to come; the step the team is on — the one in
 * progress, or else the next to come; how many are done, of how many; and
 * whether all are. The step in progress is the team's first active mission.
 */
export function missionTrack(store: TeamMissionStore): {
  steps: TrackStep[];
  current: TrackStep | undefined;
  done: number;
  total: number;
  allDone: boolean;
} {
  const track = store.track ?? [];
  const completed = store.completed_definitions ?? {};
  const active = store.active_missions?.[0];
  const steps = track.map((step): TrackStep => {
    if (completed[step.definition_id]) return { id: step.definition_id, descriptionKey: step.description_key, state: 'done' };
    if (active?.definition_id === step.definition_id) {
      const current = Math.max(0, active.current_progress);
      const target = active.target_value;
      return {
        id: step.definition_id,
        descriptionKey: step.description_key,
        state: 'active',
        progress: { current, target, fraction: target > 0 ? Math.min(1, current / target) : 0 },
      };
    }
    return { id: step.definition_id, descriptionKey: step.description_key, state: 'next' };
  });
  return {
    steps,
    current: steps.find((step) => step.state === 'active') ?? steps.find((step) => step.state === 'next'),
    done: store.completed_count,
    total: track.length,
    allDone: track.length > 0 && store.completed_count >= track.length,
  };
}

/**
 * The track as a mission's celebration shows it, from the mission store the
 * completion arrived with: each step done or still to come, and the one just
 * done picked out. The next step shows no progress: the store arrives with it
 * at 0, and the server counts it on the next update, so a figure here would
 * read "0 / 5" for a team that already flies two routes.
 */
export function celebratedTrack(store: TeamMissionStore, missionDefinitionId: string): CelebratedStep[] {
  return missionTrack(store).steps.map((step) => ({
    id: step.id,
    descriptionKey: step.descriptionKey,
    done: step.state === 'done',
    justDone: step.id === missionDefinitionId,
  }));
}

/**
 * Where the team stands on a stretch goal's metric, from the team's own
 * figures, with money kept in its currency and units. A metric the client
 * does not know reads 0. The number still supplies translation plurals.
 */
export function stretchGoalValue(team: Pick<Team, 'lifetime_passengers' | 'lifetime_km_flown' | 'cash' | 'influence'> | undefined, metric: string): { value: number; text: string } {
  const byMetric: Record<string, number | undefined> = {
    lifetime_passengers: team?.lifetime_passengers,
    lifetime_km_flown: team?.lifetime_km_flown,
    cash: team?.cash,
    influence: team?.influence,
  };
  const value = byMetric[metric] ?? 0;
  return { value, text: metric === 'cash' ? formatCash(value) : formatNumber(value) };
}

export const useMissionsStore = create<MissionsState>()(
  devtools(
    (set) => ({
      ...initialState,

      setMissionStore: (cohortId, store) => {
        set(
          (state) => ({
            missionsByTeam: {
              ...state.missionsByTeam,
              [cohortId]: store,
            },
          }),
          false,
          'setMissionStore'
        );
      },

      updateMission: (cohortId, mission) => {
        set(
          (state) => {
            const store = state.missionsByTeam[cohortId];
            if (!store) return state;

            const activeMissions = store.active_missions.map((m) =>
              m.mission_id === mission.mission_id ? mission : m
            );

            return {
              missionsByTeam: {
                ...state.missionsByTeam,
                [cohortId]: {
                  ...store,
                  active_missions: activeMissions,
                },
              },
            };
          },
          false,
          'updateMission'
        );
      },

      enqueueCelebration: (celebration) => {
        set(
          (state) => ({
            celebrationQueue: [...state.celebrationQueue, celebration],
          }),
          false,
          'enqueueCelebration'
        );
      },

      dismissCelebration: () => {
        set(
          (state) => ({
            celebrationQueue: state.celebrationQueue.slice(1),
          }),
          false,
          'dismissCelebration'
        );
      },

      setStretchGoalCelebration: (goal) => {
        set({ stretchGoalCelebration: goal }, false, 'setStretchGoalCelebration');
      },

      dismissStretchGoalCelebration: () => {
        set({ stretchGoalCelebration: null }, false, 'dismissStretchGoalCelebration');
      },

      reset: () => {
        set(initialState, false, 'reset');
      },
    }),
    {
      name: 'missions-store',
    }
  )
);

// ==================== HANDLERS ====================

/**
 * Handle initial missions state from server.
 */
export function handleMissionsState(data: MissionsStateMessage) {
  if (!isCurrentPlayerMissionUpdate(data.cohort_id)) return;
  const store = useMissionsStore.getState();
  store.setMissionStore(data.cohort_id, data.mission_store);
}

/**
 * Handle mission progress updates.
 */
export function handleMissionProgress(data: MissionProgressMessage) {
  if (!isCurrentPlayerMissionUpdate(data.cohort_id)) return;
  const store = useMissionsStore.getState();

  for (const mission of data.missions) {
    store.updateMission(data.cohort_id, mission);
  }
}

/**
 * Handle mission completion with post-advance mission_store snapshot.
 * When the last episode mission completes, also queues a stretch goal celebration.
 */
export function handleMissionCompleted(data: MissionCompletedMessage) {
  if (!isCurrentPlayerMissionUpdate(data.cohort_id)) return;
  const store = useMissionsStore.getState();
  store.setMissionStore(data.cohort_id, data.mission_store);

  if (data.team) {
    handleTeamUpdate(data.cohort_id, data.team);
  }

  const track = celebratedTrack(data.mission_store, data.mission.definition_id);
  store.enqueueCelebration({
    id: `${data.cohort_id}-${data.mission.mission_id}-${Date.now()}`,
    mission: data.mission,
    done: data.mission_store.completed_count,
    total: track.length || data.mission_store.total_steps,
    track,
  });

  const trackLen = data.mission_store.track?.length ?? 0;
  const allComplete = trackLen > 0 && data.mission_store.completed_count >= trackLen;
  if (allComplete && data.mission_store.stretch_goal) {
    store.setStretchGoalCelebration(data.mission_store.stretch_goal);
  }
}

function isCurrentPlayerMissionUpdate(cohortId: string): boolean {
  if (!getViewerUserId()) {
    console.warn('[Missions] No session user_id, skipping mission update');
    return false;
  }

  const currentPlayer = getCurrentPlayer();
  if (!currentPlayer?.cohort_id) {
    console.warn('[Missions] Player cohort not found, skipping mission update');
    return false;
  }

  return currentPlayer.cohort_id === cohortId;
}

// ==================== THE PLAYER'S ACTS ====================

/**
 * The player closes the mission's moment on screen — its Continue, or the
 * moment's own recovery when it cannot draw: the next mission done, if one
 * waits, or else every mission done (the stretch goal's).
 */
export function dismissMissionMoment(): void {
  const missions = useMissionsStore.getState();
  if (missions.celebrationQueue.length > 0) missions.dismissCelebration();
  else missions.dismissStretchGoalCelebration();
}
